import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import _set from 'lodash/set';
import { I18n } from '@app/i18n';
import { Analytics } from '@app/analytics';

interface ApiFormattedErrorResponse<T = any> {
  statusCode: number;
  code: string;
  message: string;
  errors?: T;
  meta: any;
}

interface ConstraintFailed<
  T extends { [key: string]: any },
  K extends keyof T,
> {
  target: T;
  property: keyof T;
  value: T[K];
  constraints?: {
    [constraint: string]: string;
  };
  children: ConstraintFailed<T[K], keyof T[K]>[];
}

export class ApiException<T = any> extends Error implements AxiosError<T> {
  config?: InternalAxiosRequestConfig;
  code?: string;
  request?: any;
  response?: AxiosResponse<T>;
  isAxiosError = true;
  apiException = true;
  error: AxiosError;

  constructor(e: AxiosError) {
    super(e.message);
    Object.assign(this, e);
    this.config = e.config;
    this.error = e;
  }

  toJSON() {
    return this.error.toJSON();
  }
}

export class ApiNetworkException extends ApiException<any> {
  override response?: undefined;
}

export class ApiFormattedException<T = any> extends ApiException<
  ApiFormattedErrorResponse<T>
> {
  override response: AxiosResponse<ApiFormattedErrorResponse<T>>;

  constructor(e: AxiosError<ApiFormattedErrorResponse<T>>) {
    super(e);
    if (!e.response) {
      throw Error('Try to initialize ApiFormattedException without response');
    }
    this.response = e.response;
  }

  getTranslatedMessage(defaultMessage?: string) {
    const i18nKey = `errors.${this.response.data.code}`;
    if (I18n.exists(i18nKey)) {
      return I18n.t(i18nKey, { ...this.response.data }) as string;
    }
    if (defaultMessage) {
      return I18n.t(defaultMessage, { ...this.response.data }) as string;
    }
    return null;
  }

  getMessage() {
    return this.getTranslatedMessage() || this.response.data.message;
  }

  getCode() {
    return this.response.data.code;
  }
}

export class SfApiFormattedException<T = any> extends ApiFormattedException<T> {
  override getTranslatedMessage(defaultMessage?: string) {
    if (this.response.data.message) {
      return this.response.data.message;
    }
    return super.getTranslatedMessage(defaultMessage);
  }
}

interface FormikFormatterError<T extends { [key: string]: any }> {
  [key: string]: string | FormikFormatterError<T[keyof T]>;
}

export class ApiValidationFailedException<
  T extends { [key: string]: any },
> extends ApiFormattedException<ConstraintFailed<T, keyof T>[]> {
  formikErrors: FormikFormatterError<T>;

  constructor(
    e: AxiosError<ApiFormattedErrorResponse<ConstraintFailed<T, keyof T>[]>>,
  ) {
    super(e);
    const data = this.response.data;
    if (!data.errors) {
      this.formikErrors = {};
      return;
    }

    this.formikErrors = ApiValidationFailedException.computeFormikErrors<T>(
      data.errors,
    );
  }

  static computeFormikErrors<T extends { [key: string]: any }>(
    constraints: ConstraintFailed<T, keyof T>[],
  ): FormikFormatterError<T> {
    return constraints.reduce((agg, constraintValue) => {
      const { property, constraints, value, children } = constraintValue;

      if (children && children.length) {
        return {
          ...agg,
          [property]:
            ApiValidationFailedException.computeFormikErrors<T[keyof T]>(
              children,
            ),
        };
      }

      const constraintNames = Object.keys(constraints || {});
      if (constraints && constraintNames.length > 0) {
        const constraintName = constraintNames[0];
        let message = constraints[constraintName];
        if (I18n.exists(`form.apiErrors.${constraintName}`)) {
          message = I18n.t(`form.apiErrors.${constraintName}`, { value });
        }
        if (
          I18n.exists(`form.apiErrors.${String(property)}_${constraintName}`)
        ) {
          message = I18n.t(
            `form.apiErrors.${String(property)}_${constraintName}`,
            {
              value,
            },
          );
        }
        return {
          ...agg,
          [property]: message,
        };
      }

      return agg;
    }, {});
  }

  getFormikErrors() {
    return this.formikErrors;
  }

  removeError(name: string) {
    _set(this.formikErrors, name, null);
  }
}

export class ErrorWithMessage extends Error {
  constructor(public translatedMessage: string) {
    super(translatedMessage);
  }
}

export const createApiException = <E = any>(e: AxiosError<any> | Error) => {
  if (!('config' in e)) {
    return e;
  }
  if (!e.response) {
    return new ApiNetworkException(e);
  }

  if (!e.response.data) {
    Analytics.assertionFailed('No data in the response');
    return new ApiException(e);
  }
  const data = e.response.data;
  if (!data.statusCode && data.code && data.message) {
    return new SfApiFormattedException(e);
  }

  const isExpectedAuthFailure =
    e.response.status === 401 || e.response.status === 403;

  if (!data.statusCode || !data.code || !data.message) {
    if (!isExpectedAuthFailure) {
      Analytics.warnError(e, { data }, { type: 'api' });
    }
    return new ApiException(e);
  }

  if (data.code === 'VALIDATION_FAILED') {
    return new ApiValidationFailedException(e);
  }

  if (e.response.status >= 500) {
    Analytics.warnError(e, { data }, { type: 'api', code: data.code });
  }

  return new ApiFormattedException<E>(e);
};
